import PermissionService from '@/services/PermissionsService';

/**
 * Decides whether a route can be accessed with the given permissions.
 *
 * @param {Object} to - Vue Router route being resolved.
 * @param {Object} context
 * @param {boolean} context.isAdmin - Whether the user owns the selected organization.
 * @param {Array<string>|undefined} context.permissions - Permissions loaded for the user.
 * @returns {boolean}
 */
export function isRouteAllowed(to, { isAdmin, permissions }) {
  const user = { permissions };

  return to.matched.every((record) => {
    const { requiresAdmin, permission } = record.meta;

    if (!requiresAdmin && !permission) {
      return true;
    }

    if (isAdmin) {
      return true;
    }

    if (!permission) {
      return false;
    }

    const requiredPermissions = Array.isArray(permission)
      ? permission
      : [permission];

    return requiredPermissions.some((requiredPermission) =>
      PermissionService.can(user, requiredPermission)
    );
  });
}

/**
 * Builds the navigation guard used by the router.
 *
 * The guard runs on the very first navigation, before the root instance loads
 * the user, so it must wait for the permissions bootstrap instead of evaluating
 * an empty permission list — otherwise every protected route redirects to the
 * account area on page refresh.
 *
 * @param {Object} options
 * @param {Object} options.store - Vuex store instance.
 * @param {Function} options.bootstrapUser - Resolves (and commits) the user permissions.
 * @returns {Function} Vue Router beforeEach guard.
 */
export function createRouteGuard({ store, bootstrapUser }) {
  return async (to, from, next) => {
    const isPublicRoute =
      String(to.name ?? '')
        .split('.')
        .shift() === 'public';

    if (!isPublicRoute && !store.state.userPermissions.loaded) {
      try {
        await bootstrapUser();
      } catch (error) {
        // Auth errors are handled by the root instance (401/412 redirect).
      }
    }

    const allowed = isRouteAllowed(to, {
      isAdmin: store.getters['organizations/getIsOwner'],
      permissions: store.state.userPermissions.permissions,
    });

    if (!allowed) {
      if (window.location.pathname.includes('/business')) {
        // Go to dashboard page
        return next({ name: 'business.dashboard' });
      }
      return next({ name: 'account.profile' });
    }

    return next();
  };
}

export default createRouteGuard;
